import React from 'react'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { Pair, TokenAmount, Currency } from 'vvs-sdk'
import { Text, Flex, Heading, OverallLiquidityInfoIcon } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import Balance from 'components/Balance'
import LocalBalance from 'components/LocalBalance'
import getFiatSymbol from 'utils/getFiatSymbol'
import getFiatValue from 'utils/getFiatValue'
import { BIG_ZERO } from 'utils/bigNumber'
import { unwrappedToken } from 'utils/wrappedCurrency'
import { formatNumber } from 'utils/formatBalance'
import { useTokenBalance } from 'state/wallet/hooks'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import useTotalSupply from 'hooks/useTotalSupply'
import useTheme from 'hooks/useTheme'

export interface LiquidityPoolProps {
  lpLabel: string
  lpTokenPrice: BigNumber
  stakedLPAmount: BigNumber
  pair: Pair
}

const Wrapper = styled.div`
  margin-top: 24px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.8fr 0.2fr;
  margin-top: 16px;
  margin-bottom: 16px;
`

const LPRow = styled.div`
  display: grid;
  grid-template-columns: 1.7fr 1.3fr 0.4fr;
  margin-top: 16px;
  margin-bottom: 16px;
  border-top: solid;
  border-color: #f4f5f1;
`

const LiquidityPool: React.FC<LiquidityPoolProps> = ({ lpLabel, lpTokenPrice, stakedLPAmount, pair }) => {
  const { t } = useTranslation()
  const { account } = useActiveWeb3React()
  const { theme } = useTheme()
  const symbol: string = getFiatSymbol()

  // unwrapped pair tokens
  const token0: Currency = unwrappedToken(pair.token0)
  const token1: Currency = unwrappedToken(pair.token1)

  // lp token price
  const lpTokenPriceDisplay: string = lpTokenPrice.gte(1)
    ? formatNumber(getFiatValue(lpTokenPrice).toNumber())
    : getFiatValue(lpTokenPrice).toPrecision(2)

  // token reserve in pool
  const token0Reserve: TokenAmount = pair.reserve0
  const token1Reserve: TokenAmount = pair.reserve1

  // calculate share of pool
  const userNonstakeLPAmount: TokenAmount = useTokenBalance(account ?? undefined, pair.liquidityToken)
  const totalPoolTokens: TokenAmount = useTotalSupply(pair.liquidityToken)

  const poolTokenPercentage =
    userNonstakeLPAmount && totalPoolTokens && !totalPoolTokens.equalTo('0')
      ? stakedLPAmount
          .plus(new BigNumber(userNonstakeLPAmount.toFixed()))
          .div(new BigNumber(totalPoolTokens.toFixed()))
          .div(100)
      : undefined

  // calculate total lp token worth in the lp token contract
  const tvl: BigNumber =
    totalPoolTokens !== undefined ? lpTokenPrice.times(totalPoolTokens.toSignificant(18)) : BIG_ZERO

  const token0PerLP: string =
    totalPoolTokens !== undefined ? token0Reserve.divide(totalPoolTokens).toSignificant(18) : '0.00'
  const token1PerLP: string =
    totalPoolTokens !== undefined ? token1Reserve.divide(totalPoolTokens).toSignificant(18) : '0.00'

  return (
    <Wrapper>
      <Flex mt="20px" mb="20px">
        <OverallLiquidityInfoIcon mr="8px" />
        <Heading>{t('Overall Liquidity Information')}</Heading>
      </Flex>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('LP token price')}
          </Text>
        </Flex>
        <Text>{`${symbol}${lpTokenPriceDisplay}`}</Text>
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Total %tokenSymbol%', {
              tokenSymbol: token0.symbol,
            })}
          </Text>
        </Flex>
        <Balance
          decimals={6}
          color={theme.colors.text}
          prefix=""
          value={new BigNumber(token0Reserve.toFixed(6)).toNumber()}
        />
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Total %tokenSymbol%', {
              tokenSymbol: token1.symbol,
            })}
          </Text>
        </Flex>
        <Balance
          decimals={6}
          color={theme.colors.text}
          prefix=""
          value={new BigNumber(pair?.reserve1.toFixed(6)).toNumber()}
        />
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('TVL')}
          </Text>
        </Flex>
        <LocalBalance decimals={2} color={theme.colors.text} prefix="" value={tvl} />
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Total LP tokens')}
          </Text>
        </Flex>
        {totalPoolTokens !== undefined ? (
          <Balance
            decimals={6}
            color={theme.colors.text}
            prefix=""
            value={new BigNumber(totalPoolTokens.toSignificant(18)).toNumber()}
          />
        ) : (
          <Text>0.000</Text>
        )}
        <Flex />
      </Row>
      <Row>
        <Flex mt="3px">
          <Text color="textSubtle" fontSize="13px">
            {t('Share of Pool')}
          </Text>
        </Flex>
        {poolTokenPercentage && !poolTokenPercentage.isEqualTo(0) ? (
          <Text>{`(${poolTokenPercentage?.toFixed(2) === '0.00' ? '<0.01' : poolTokenPercentage?.toFixed(2)}%)`}</Text>
        ) : (
          <Text>(0.00%)</Text>
        )}
        <Flex />
      </Row>
      <LPRow>
        <Flex mt="10px">
          <Text color="textSubtle" bold fontSize="20px">
            {`1 ${lpLabel}`}
          </Text>
        </Flex>
        <Flex mt="10px">
          {totalPoolTokens !== undefined ? (
            <Text bold fontSize="20px">
              {`${formatNumber(new BigNumber(token0PerLP).toNumber(), 6, 6)} ${token0.symbol}
              + ${formatNumber(new BigNumber(token1PerLP).toNumber(), 6, 6)} ${token1.symbol}`}
            </Text>
          ) : (
            <Text />
          )}
          <Text />
        </Flex>
      </LPRow>
    </Wrapper>
  )
}

export default LiquidityPool
